import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
    FormControlLabel, FormGroup, Switch, makeStyles,
} from '@material-ui/core';

import { hasValue, isEmpty } from '../../objectUtils';
import { formAnswerTypes } from '../../constants';
import HtmlContent from '../HtmlContent';
import TableResponse from './formResponseTypes/TableResponse';
import ScaleResponse from './formResponseTypes/ScaleResponse';
import FreetextResponse from './formResponseTypes/FreetextResponse';
import SingleChoice from './formResponseTypes/SingleChoice';
import MultipleChoice from './formResponseTypes/MultipleChoice';
import ActionButton from './formResponseTypes/ActionButton';

const useStyles = makeStyles(theme => ({
    root: {
        '&+&': {
            marginTop: theme.spacing(5),
        },
    },
    questionText: {
        marginBottom: theme.spacing(1),
        fontSize: theme.typography.body1.fontSize,

        '&:empty': {
            marginBottom: 0,
        },
    },
    required: {
        border: `1px solid ${theme.palette.error.main}`,
        padding: theme.spacing(1),
    },
    switchResponse: {
        padding: theme.spacing(2),
    },
}));

export const hasValueForQuestionType = (questionType, response) => {
    if (questionType === formAnswerTypes.action) {
        return true;
    }
    if (!response || isEmpty(response)) {
        return false;
    }
    switch (questionType) {
    case formAnswerTypes.freetext:
        return hasValue(response.freetextResponse);
    case formAnswerTypes.singleChoice:
    case formAnswerTypes.multipleChoice:
        return hasValue(response.choiceResponse);
    case formAnswerTypes.scale:
        return hasValue(response.scaleResponse);
    case formAnswerTypes.table:
        return hasValue(response.tableResponses);
    default:
        return true;
    }
};

const FormQuestion = ({
    response, handleResponseUpdate, showScores, translations, question, showRequired, disabled, contentState, ScaleResponseProps, readOnly, elementSettings,
}) => {
    const classes = useStyles();

    const handleValueChange = name => (value) => {
        handleResponseUpdate(name, value);
    };

    const handleClickResponse = (name, value) => () => {
        if (readOnly) return;
        handleResponseUpdate(name, value);
    };

    if (question === null) {
        return null;
    }

    return (
        <div className={classnames(classes.root, { [classes.required]: question.required && showRequired && !hasValueForQuestionType(question.type, response) })}>
            {question.type !== formAnswerTypes.action && question.type !== formAnswerTypes.switch && (
                <HtmlContent html={question.question} className={classes.questionText} />
            )}

            {question.type === formAnswerTypes.freetext && (
                <FreetextResponse
                    handleResponseUpdate={handleValueChange('freetextResponse')}
                    freetextConfig={question.freetextConfig}
                    freetextResponse={response?.freetextResponse}
                    translations={translations}
                    disabled={disabled}
                    readOnly={readOnly}
                />
            )}

            {(question.type === formAnswerTypes.singleChoice || question.type === 'ActionChoice') && ( // ActionChoice is obsolete..
                <SingleChoice
                    handleResponseUpdate={handleValueChange('choiceResponse')}
                    choiceConfig={question.choiceConfig}
                    selectedChoice={response?.choiceResponse && response.choiceResponse[0]}
                    question={question}
                    translations={translations}
                    disabled={disabled}
                    showScores={showScores}
                    readOnly={readOnly}
                />
            )}

            {question.type === formAnswerTypes.multipleChoice && (
                <MultipleChoice
                    handleResponseUpdate={handleValueChange('choiceResponse')}
                    choiceConfig={question.choiceConfig}
                    selectedChoices={response?.choiceResponse}
                    question={question}
                    translations={translations}
                    disabled={disabled}
                    readOnly={readOnly}
                    showScores={showScores}
                />
            )}

            {(question.type === formAnswerTypes.scale || question.type === 'ActionScale') && ( // ActionScale is obsolete..
                <ScaleResponse
                    scaleConfig={question.scaleConfig}
                    response={response?.scaleResponse}
                    disabled={disabled}
                    readOnly={readOnly}
                    handleResponseUpdate={handleValueChange('scaleResponse')}
                    {...ScaleResponseProps}
                />
            )}

            {question.type === formAnswerTypes.table && (
                <TableResponse
                    tableConfig={question.tableConfig}
                    response={response?.tableResponses}
                    disabled={disabled}
                    readOnly={readOnly}
                    elementSettings={elementSettings}
                    handleResponseUpdate={handleValueChange('tableResponses')}
                />
            )}

            {question.type === formAnswerTypes.action && (
                <ActionButton
                    handleResponseUpdate={handleValueChange('scaleResponse')}
                    scaleResponse={response?.scaleResponse}
                    contentState={contentState}
                    question={question}
                    disabled={disabled}
                    readOnly={readOnly}
                />
            )}

            {question.type === formAnswerTypes.switch && (
                <div className={classes.switchResponse}>
                    <FormGroup>
                        <FormControlLabel
                            disabled={disabled}
                            control={(
                                <Switch
                                    color="secondary"
                                    checked={!!response?.scaleResponse}
                                    onChange={handleClickResponse('scaleResponse', response?.scaleResponse ? 0 : 1)}
                                />
                            )}
                            label={<HtmlContent html={question.question} />}
                        />
                    </FormGroup>
                </div>
            )}

        </div>
    );
};

FormQuestion.propTypes = {
    translations: PropTypes.object,
    handleResponseUpdate: PropTypes.func.isRequired,
    question: PropTypes.object.isRequired,
    response: PropTypes.object,
    showRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    contentState: PropTypes.string,
    ScaleResponseProps: PropTypes.object,
    showScores: PropTypes.bool,
    readOnly: PropTypes.bool,
    elementSettings: PropTypes.object,
};

FormQuestion.defaultProps = {
    response: null,
    showRequired: false,
    disabled: false,
    contentState: null,
    translations: null,
    ScaleResponseProps: null,
    showScores: false,
    readOnly: false,
    elementSettings: null,
};

export default FormQuestion;
